import React, { useEffect, useMemo, useState } from 'react';
import { RiSearchLine, RiLoader4Line, RiCheckboxCircleFill } from '@remixicon/react';
import Modal from '@components/ui/Modal';
import { apiClient } from '@/lib/api';

/**
 * Search + pick dialog for aircraft_make_models. The BE combines multiple
 * filter params with AND, but the broker mental-model is OR ("Falcon OR
 * Helicopter"), so when BOTH manufacturer and aircraft_type are filled
 * we fire TWO parallel GET calls and union the results (dedupe by id).
 *
 * On select → onSelect(row) — parent hydrates its make/model form fields
 * and stashes the row for "dirty" detection at save time.
 */
const MakeModelSearchDialog = ({ isOpen, onClose, onSelect }) => {
  const [manufacturerQ, setManufacturerQ] = useState('');
  const [aircraftTypeQ, setAircraftTypeQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Reset local state each open — matches the "picking a new model" UX.
  useEffect(() => {
    if (!isOpen) return;
    setManufacturerQ('');
    setAircraftTypeQ('');
    setResults([]);
    setHasSearched(false);
  }, [isOpen]);

  const canSearch = useMemo(
    () => manufacturerQ.trim().length > 0 || aircraftTypeQ.trim().length > 0,
    [manufacturerQ, aircraftTypeQ],
  );

  const runSearch = async () => {
    if (!canSearch) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const both = manufacturerQ.trim() && aircraftTypeQ.trim();
      let rows = [];
      if (both) {
        // BE ANDs the filters; fire two calls and union for OR semantics.
        const [a, b] = await Promise.all([
          apiClient.getAircraftMakeModels({ manufacturer: manufacturerQ.trim(), limit: 100 }),
          apiClient.getAircraftMakeModels({ aircraft_type: aircraftTypeQ.trim(), limit: 100 }),
        ]);
        const map = new Map();
        [...(a || []), ...(b || [])].forEach((r) => {
          const id = r?.aircraft_make_model_id;
          if (id && !map.has(id)) map.set(id, r);
        });
        rows = [...map.values()];
      } else {
        const filters = { limit: 100 };
        if (manufacturerQ.trim()) filters.manufacturer = manufacturerQ.trim();
        if (aircraftTypeQ.trim()) filters.aircraft_type = aircraftTypeQ.trim();
        const r = await apiClient.getAircraftMakeModels(filters);
        rows = Array.isArray(r) ? r : (r?.data || []);
      }
      setResults(rows);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('MakeModelSearchDialog: search failed', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title="Search Make / Model"
      headerClassName="!border-0"
      closeOnOutsideClick
    >
      <div className="p-6 -mt-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Manufacturer</label>
            <input
              type="text"
              value={manufacturerQ}
              onChange={(e) => setManufacturerQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              placeholder="e.g. Dassault"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Aircraft Type</label>
            <input
              type="text"
              value={aircraftTypeQ}
              onChange={(e) => setAircraftTypeQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
              placeholder="e.g. Helicopter"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={runSearch}
            disabled={!canSearch || loading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-700 hover:bg-primary-800 disabled:bg-gray-300 text-white text-sm transition-colors"
          >
            {loading ? <RiLoader4Line className="w-4 h-4 animate-spin" /> : <RiSearchLine className="w-4 h-4" />}
            Search
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto -mx-2 px-2">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-gray-400 text-sm">
              <RiLoader4Line className="w-4 h-4 animate-spin mr-2" /> Searching…
            </div>
          ) : results.length === 0 && hasSearched ? (
            <div className="py-12 text-center text-sm text-gray-400">
              No matches. Fill the form on Tab 2 to create a new one.
            </div>
          ) : results.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400">
              Enter a manufacturer or aircraft type to search.
            </div>
          ) : (
            <div className="space-y-2">
              {results.map((row) => (
                <button
                  key={row.aircraft_make_model_id}
                  type="button"
                  onClick={() => { onSelect?.(row); onClose?.(); }}
                  className="w-full flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:border-primary-300 hover:bg-primary-50/40 text-left transition-colors"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                    <RiCheckboxCircleFill className="w-4 h-4 text-gray-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {row.manufacturer} · {row.model_name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {row.aircraft_type}
                      {row.make_code && <> · {row.make_code}</>}
                      {row.model_code && <>/{row.model_code}</>}
                      {(row.min_range || row.max_range) && (
                        <> · {row.min_range || '?'}–{row.max_range || '?'} nm</>
                      )}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default MakeModelSearchDialog;
